import { Center, HStack, Heading, Spacer } from "@chakra-ui/react";
import { BsRecord2 as RecordIcon } from "react-icons/bs";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";
import { Transition } from "react-transition-group";

import { useScreenRecorder } from "@nens/react/components";
import * as api from "@nens/threedi/openapi-client";

import { AppDispatch } from "../App";
import { RanaLiveIcon } from "../components/rana-live-logo";
import StatusContainer from "../components/StatusContainer";
import PauseIcon from "../icons/pause_icon.svg?react";
import PlayIcon from "../icons/PlayIcon";
import UserIcon from "../icons/user_icon.svg?react";
import { checkPermission } from "../state/appState";
import {
  ExistingWindConstant,
  RainEvent,
  getCurrentWind,
  getExistingEvents,
  rainEventTimeCalculator,
} from "../state/existingEvents";
import {
  continueFlashPlayButton,
  getFlashPlayButton,
  getUserMenuOpen,
  setUserMenuOpen,
  stopFlashPlayButton,
} from "../state/mainGui";
import {
  getLoad,
  getSimulation,
  getSimulationStateChangeFailed,
  getSimulationStateChangeRequested,
  getSimulationStatus,
  getThreediModel,
  pauseSimulation,
  startSimulation,
} from "../state/simulation";

import styles from "./Header.module.css";
import iconButtonStyles from "./IconButton.module.css";
import UserMenu from "./UserMenu";

const Header: React.FC<ReturnType<typeof mapDispatchToProps>> = (props) => {
  const { recording, switchRecordingActive } = useScreenRecorder();
  const load = useSelector(getLoad);
  const threediModel = useSelector(getThreediModel);
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);
  const simulationStateChangeRequested = useSelector(getSimulationStateChangeRequested);
  const simulationStateChangeFailed = useSelector(getSimulationStateChangeFailed);
  const userMenuOpen = useSelector(getUserMenuOpen);
  const existingRainEvents = useSelector(getExistingEvents);
  const arrayOfAllExistingRainEvents = [
    ...existingRainEvents.constantrain,
    ...existingRainEvents.lizardrasterrain,
    ...existingRainEvents.timeseriesrain,
  ];
  const flashPlayButton = useSelector(getFlashPlayButton);

  const getOffset = (): number => {
    return simulationStatus.time || 0;
  };
  const maybeCurrentWind = useSelector(getCurrentWind)(getOffset());

  // helper function to determine remaining time of longest rain event
  const getLongestRemainingTimeFromActiveRainEvents = (
    rainEvents: RainEvent[],
    simulationRunningTime: number // in seconds
  ) => {
    const remainingTimesOfRainEvents = rainEvents.map((event) => {
      return rainEventTimeCalculator(event, simulationRunningTime);
    });

    // get the longest remaining time to show in the header
    return remainingTimesOfRainEvents.length > 0 ? Math.max(...remainingTimesOfRainEvents) : 0;
  };
  const remainingTimeOfRainEvents = getLongestRemainingTimeFromActiveRainEvents(
    arrayOfAllExistingRainEvents,
    simulationStatus.time
  );

  const intl = useIntl();
  const crashTitle = intl.formatMessage(
    {
      id: "header.crashed_title",
      defaultMessage: "Crashed. Use simulation-id: {simulationId} for support",
    },
    { simulationId: simulation ? simulation.id + "" : "Simulation id unknown" }
  );
  // In some situations the play / pause button shouldn't be shown
  const startStopAllowed = useSelector(checkPermission)("startStopSimulation").allowed;

  return (
    <HStack
      as="header"
      gap="32px"
      justifyContent="space-between"
      alignItems="center"
      zIndex={1000}
      padding="0 16px"
      height="60px"
      className={styles.Header}
    >
      <RanaLiveIcon width="235px" height="26px" />
      <Heading
        as="h3"
        fontSize="18px"
        overflowX="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {simulationStatus.status === api.SimulationStatusNameEnum.CRASHED
          ? crashTitle
          : threediModel?.name}
      </Heading>
      <Spacer />
      <HStack gap="4px">
        {startStopAllowed ? (
          <Center bg="#fff" w="48px" h="48px" borderRadius="standard">
            {/* Note the two buttons (play and pause) are both
                rendered, one has display: none. If we use ? : to
                render one instead, that leads to problems with the
                Transition still rendering a button that should
                already be gone.  */}
            <Transition
              in={flashPlayButton % 2 > 0}
              timeout={200}
              onEntered={props.continueFlashPlayButton}
              onExited={props.continueFlashPlayButton}
            >
              {(state) => {
                const transitionStyles: {
                  [state: string]: React.CSSProperties;
                } = {
                  entering: { transform: "scale(2)" },
                  entered: { transform: "scale(2)" },
                  exiting: { transform: "scale(1)" },
                  exited: { transform: "scale(1)" },
                };
                return (
                  <button
                    className={`${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton} ${iconButtonStyles.NotHighlighted}`}
                    title={intl.formatMessage({
                      id: "header.play_button",
                      defaultMessage: "Start simulation",
                    })}
                    onClick={() => {
                      props.startSimulation();
                      props.stopFlashPlayButton();
                    }}
                    disabled={simulationStateChangeRequested}
                    style={{
                      cursor: simulationStateChangeRequested ? "not-allowed" : "pointer",
                      opacity: simulationStateChangeRequested ? 0.5 : 1,
                      transition: "transform 0.2s",
                      display: simulationStatus.status === "running" ? "none" : undefined,
                      ...(simulationStateChangeRequested ? {} : transitionStyles[state]),
                    }}
                  >
                    <PlayIcon />
                  </button>
                );
              }}
            </Transition>
            <button
              className={`${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton} ${iconButtonStyles.NotHighlighted}`}
              onClick={props.pauseSimulation}
              disabled={simulationStateChangeRequested}
              style={{
                cursor: simulationStateChangeRequested ? "not-allowed" : "pointer",
                opacity: simulationStateChangeRequested ? 0.5 : 1,
                display: simulationStatus.status === "running" ? undefined : "none",
              }}
            >
              <PauseIcon />
            </button>
          </Center>
        ) : null}
        <StatusContainer
          type={"SIMULATION"}
          simulationStatus={simulationStatus}
          setSimulationToPaused={props.pauseSimulation}
        />
        <Center bg="#fff" w="48px" h="48px" borderRadius="standard">
          <RecordIcon
            onClick={switchRecordingActive}
            cursor="pointer"
            color={recording ? "red" : undefined}
            size={32}
          ></RecordIcon>
        </Center>
        <span className={styles.PlayPauseStatus}>
          {/* Different texts to display status of play/pause request based upon
              simulationStateChangeRequested and current status of the simulation */}
          {simulationStateChangeRequested &&
          (simulationStatus.status === "paused" || simulationStatus.status === "initialized") ? (
            <FormattedMessage id="header.play_pause_status_starting" defaultMessage="Starting" />
          ) : (
            ""
          )}
          {simulationStateChangeRequested && simulationStatus.status === "running" ? (
            <FormattedMessage id="header.play_pause_status_pausing" defaultMessage="Pausing" />
          ) : (
            ""
          )}
          {!simulationStateChangeRequested &&
          !simulationStateChangeFailed &&
          simulationStatus.status === "paused" ? (
            <FormattedMessage id="header.play_pause_status_paused" defaultMessage="Paused" />
          ) : (
            ""
          )}
          {!simulationStateChangeRequested &&
          simulationStateChangeFailed &&
          (simulationStatus.status === "paused" || simulationStatus.status === "initialized") ? (
            <FormattedMessage
              id="header.play_pause_failed_to_start_simulation"
              defaultMessage="Failed to start"
            />
          ) : (
            ""
          )}
          {!simulationStateChangeRequested &&
          simulationStateChangeFailed &&
          simulationStatus.status === "running" ? (
            <FormattedMessage
              id="header.play_pause_failed_to_pause_simulation"
              defaultMessage="Failed to pause"
            />
          ) : (
            ""
          )}
        </span>
      </HStack>
      <div className={styles.SessionDetailsContainer}>
        <StatusContainer
          type={"RAIN"}
          rainDuration={remainingTimeOfRainEvents}
          hidden={remainingTimeOfRainEvents === 0}
          setSimulationToPaused={props.pauseSimulation}
        />
        {
          <StatusContainer
            type={"WIND"}
            rainDuration={
              maybeCurrentWind && (maybeCurrentWind as ExistingWindConstant).end
                ? (maybeCurrentWind as ExistingWindConstant).end - getOffset()
                : 0
            }
            hidden={!maybeCurrentWind}
            setSimulationToPaused={props.pauseSimulation}
          />
        }
        <StatusContainer
          type={"POWER"}
          power={simulationStatus.status === "running" && load ? load * 100 : load ? 0 : undefined}
          setSimulationToPaused={props.pauseSimulation}
        />
        <Center w="48px" h="48px" borderRadius="standard" bgColor="#fff">
          <button
            title={intl.formatMessage({
              id: "header.user_icon_tooltip",
              defaultMessage: "User menu",
            })}
            className={`${iconButtonStyles.Button} ${iconButtonStyles.Headerbutton} ${
              userMenuOpen ? iconButtonStyles.Highlighted : iconButtonStyles.NotHighlighted
            }`}
            style={{
              width: "38px",
              height: "38px",
            }}
            onClick={() => {
              props.setUserMenuOpen(!userMenuOpen);
              // No need to prevent bubbling to root elements , since
              //  at this moment the store is not updated yet, thus userMenuOpen is still false (since store update happens asynchronous).
              // see "props.setUserMenuOpen(false);" in Header.tsx
            }}
          >
            <UserIcon />
          </button>
        </Center>
        <div
          className={`${styles.UserMenu} ${
            userMenuOpen ? styles.UserMenuOpen : styles.UserMenuClosed
          }`}
        >
          <UserMenu setSimulationToPaused={props.pauseSimulation} />
        </div>
        <div></div>
      </div>
    </HStack>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    startSimulation: () => dispatch(startSimulation()),
    pauseSimulation: () => dispatch(pauseSimulation()),
    setUserMenuOpen: (userMenuOpen: boolean) => dispatch(setUserMenuOpen(userMenuOpen)),
    continueFlashPlayButton: () => dispatch(continueFlashPlayButton()),
    stopFlashPlayButton: () => dispatch(stopFlashPlayButton()),
  };
};

export default connect(null, mapDispatchToProps)(Header);
